import React, { useEffect, useRef } from "react";
import Footer from "./Footer";
import Navbar from "./Navbar";
import ApplyNow from "./ApplyNow";
import image1 from "../assets/image1.jpg";
import { FaCheckCircle, FaShieldAlt } from "react-icons/fa";
import WhyChooseUs from "./WhyChooseUs";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const AboutUs: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionsRef = useRef<(HTMLElement | HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      sectionsRef.current.filter(Boolean).forEach((section, i) => {
        gsap.from(section, {
          y: 60,
          opacity: 0,
          duration: 1.2,
          ease: "power4.out",
          scrollTrigger: {
            trigger: section,
            start: "top 85%",
            toggleActions: "play none none none",
          },
          delay: i * 0.1,
          clearProps: "all",
        });
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} className="min-w-screen bg-[var(--background-gradient-middle)]">
      <div className="flex w-full flex-col items-center justify-center gap-12 px-4 py-16">
        <Navbar />

        <main className="w-full max-w-[1440px] px-4 sm:px-6 lg:px-12 mx-auto flex flex-col gap-12 md:gap-20 mt-8 md:mt-16">
          {/* Hero Section */}
          <section
            ref={(el) => { sectionsRef.current[0] = el; }}
            className="group relative text-center py-10 md:py-20 rounded-[24px] md:rounded-[48px] shadow-2xl px-5 md:px-12 overflow-hidden border-2 border-white/50 bg-white/40 backdrop-blur-3xl"
          >
            <img
              src={image1}
              alt={`${import.meta.env.VITE_COMPANY_NAME} personal loan support`}
              className="absolute inset-0 w-full h-full object-cover opacity-10 group-hover:scale-105 transition-transform duration-[2s]"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-[var(--primary)]/5 to-white/70"></div>

            <div className="relative z-10">
              <h1 className="text-3xl sm:text-5xl md:text-6xl font-black mb-6 bg-gradient-to-r from-[var(--primary)] to-[var(--secondary-accent)] bg-clip-text text-transparent leading-[1.2] md:leading-[0.9] tracking-tighter">
                About Us
              </h1>
              <div className="flex flex-col gap-4 max-w-3xl mx-auto">
                <p className="text-base sm:text-lg md:text-xl leading-relaxed text-[var(--text-gray)] font-bold">
                  Welcome to <span className="text-[var(--text-primary)] font-black">{import.meta.env.VITE_COMPANY_NAME}</span>,
                  your trusted partner in personal finance solutions. We provide fast, convenient, and short-term unsecured personal loans for salaried individuals across India.
                </p>
                <p className="text-base sm:text-lg md:text-xl leading-relaxed text-[var(--text-gray)] font-bold">
                  Our mission is to empower our customers with access to credit by simplifying the loan process using innovative technology and personalized financial solutions.
                </p>
              </div>
            </div>
          </section>

          {/* Mission & Vision Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
            {[
              {
                title: "Our Mission",
                desc: "Make personal loans quicker, simpler, and easily accessible for salaried individuals. Empower people financially with easy and affordable access to credit through innovative technology.",
                icon: <FaShieldAlt className="text-3xl md:text-4xl text-[var(--primary)] mb-4 md:mb-6" />,
              },
              {
                title: "Our Vision",
                desc: "Create a transparent, customer-centric lending ecosystem providing credit access when needed the most. Leverage technology to promote financial inclusion for all.",
                icon: <FaCheckCircle className="text-3xl md:text-4xl text-[var(--secondary)] mb-4 md:mb-6" />,
              },
            ].map((item, idx) => (
              <div
                key={idx}
                ref={(el) => { sectionsRef.current[idx + 1] = el; }}
                className="group relative bg-white/60 backdrop-blur-3xl rounded-[32px] md:rounded-[56px] p-8 md:p-16 border-2 border-white/50 shadow-[20px_20px_40px_rgba(0,0,0,0.03),-20px_-20px_40px_rgba(255,255,255,0.4)] hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-24 h-24 md:w-32 md:h-32 bg-[var(--primary)] opacity-[0.03] rounded-bl-full group-hover:scale-125 transition-transform duration-700" />
                {item.icon}
                <h2 className="text-2xl md:text-4xl font-black mb-4 md:mb-6 text-[var(--text-primary)] tracking-tight leading-none">
                  {item.title}
                </h2>
                <p className="text-[var(--text-gray)] text-lg md:text-xl font-bold leading-snug">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Technology Section */}
          <div
            ref={(el) => { sectionsRef.current[3] = el; }}
            className="group relative bg-[var(--text-primary)] backdrop-blur-3xl rounded-[24px] md:rounded-[64px] p-6 md:p-20 shadow-2xl border-2 border-white/10 overflow-hidden"
          >
            <div className="absolute top-[-10%] right-[-10%] w-64 h-64 md:w-96 md:h-96 bg-[var(--primary)] opacity-40 rounded-full blur-[60px] md:blur-[100px] pointer-events-none" />

            <div className="relative z-10 grid lg:grid-cols-2 gap-10 md:gap-16 items-center">
              <div className="text-left">
                <h2 className="text-3xl md:text-6xl font-black mb-6 md:mb-8 text-white leading-[1.1] md:leading-none tracking-tight">
                  Our<br /><span className="text-[var(--secondary)]">Technology.</span>
                </h2>
                <p className="text-white/70 text-base md:text-xl mb-8 md:mb-12 font-bold leading-relaxed max-w-lg">
                  {import.meta.env.VITE_COMPANY_NAME} is powered by advanced algorithms and a fully digital platform — simplifying loan approvals, ensuring data security, and providing accurate credit assessments within minutes.
                </p>
                <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs md:text-sm font-black uppercase tracking-widest group-hover:gap-5 transition-all cursor-pointer">
                  Next Gen Systems <span className="text-[var(--secondary)]">→</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                {[
                  "Quick and accurate credit assessments using AI algorithms.",
                  "Intuitive digital platform for seamless loan applications.",
                  "Secure and encrypted transactions for complete data safety.",
                  "Track your loan application in real time — anytime, anywhere."
                ].map((text, i) => (
                  <div key={i} className="bg-white/5 backdrop-blur-xl rounded-[20px] md:rounded-[24px] p-5 md:p-6 border border-white/10 group-hover:bg-white/10 transition-colors h-full flex flex-col justify-start">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-[var(--secondary)]/20 mb-3 md:mb-4 flex items-center justify-center text-[var(--secondary)] shrink-0">
                      <FaShieldAlt className="text-sm md:text-base" />
                    </div>
                    <span className="text-white font-black text-[10px] md:text-xs uppercase tracking-widest leading-normal">{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <WhyChooseUs />
          <ApplyNow />
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default AboutUs;
